import type { AgentStatus } from 'agentic_chat/types';

import createStore from './store';

/**
 * AI Edits Store - Reactive UI state for AI Edits functionality
 *
 * This store manages UI state that needs to be reactive across content-studio.
 *
 * State:
 * - agentStatus: Current AI agent status (Idle, Planning, Processing, etc.)
 * - isSendingMessage: Whether a message is being sent (disables UI)
 * - isAIEditsSidebarOpen: Whether AI Edits sidebar is open
 */

export interface AIEditsStoreState {
  agentStatus: AgentStatus;
  isSendingMessage: boolean;
  isAIEditsSidebarOpen: boolean;
  sidebarOpenTrigger?: 'AutoOpen' | 'ManualClick';
  hadInteraction: boolean;
  /** Width (px) of the AI Edits / Co-edit sidebar; user-resizable. */
  sidebarWidth: number;
  /** True while the user is dragging the sidebar resize handle. */
  isResizingSidebar: boolean;
}

export const AI_EDITS_SIDEBAR_MIN_WIDTH = 320;
export const AI_EDITS_SIDEBAR_MAX_WIDTH = 800;
/** Fallback default when the viewport size is unavailable (e.g. SSR / tests without a window). */
export const AI_EDITS_SIDEBAR_DEFAULT_WIDTH = 480;

/** Default the Co-edit sidebar to ~30% of the viewport, clamped to the min/max range. */
export const getDefaultSidebarWidth = (): number => {
  if (typeof window === 'undefined' || !window.innerWidth) {
    return AI_EDITS_SIDEBAR_DEFAULT_WIDTH;
  }
  return Math.min(
    Math.max(Math.round(window.innerWidth * 0.3), AI_EDITS_SIDEBAR_MIN_WIDTH),
    AI_EDITS_SIDEBAR_MAX_WIDTH
  );
};

const initialState: AIEditsStoreState = {
  agentStatus: { state: 'Idle' },
  isSendingMessage: false,
  isAIEditsSidebarOpen: false,
  sidebarOpenTrigger: undefined,
  hadInteraction: false,
  sidebarWidth: getDefaultSidebarWidth(),
  isResizingSidebar: false
};

const store = createStore<AIEditsStoreState>(initialState);
export const aiEditsStore = {
  ...store,
  setAgentStatus: (status: AgentStatus) =>
    store.update(draft => {
      draft.agentStatus = status;
    }),
  setIsSendingMessage: (sending: boolean) =>
    store.update(draft => {
      draft.isSendingMessage = sending;
    }),
  setIsAIEditsSidebarOpen: (open: boolean, trigger?: 'AutoOpen' | 'ManualClick') =>
    store.update(draft => {
      draft.isAIEditsSidebarOpen = open;
      if (open && trigger) {
        draft.sidebarOpenTrigger = trigger;
        draft.hadInteraction = false;
      }
    }),
  setHadInteraction: (hadInteraction: boolean) =>
    store.update(draft => {
      draft.hadInteraction = hadInteraction;
    }),
  setSidebarWidth: (width: number) =>
    store.update(draft => {
      draft.sidebarWidth = Math.min(Math.max(width, AI_EDITS_SIDEBAR_MIN_WIDTH), AI_EDITS_SIDEBAR_MAX_WIDTH);
    }),
  setIsResizingSidebar: (resizing: boolean) =>
    store.update(draft => {
      draft.isResizingSidebar = resizing;
    }),
  reset: () => store.set(() => initialState)
};
